import React from 'react';
import { useDispatch } from 'react-redux';
import { Col, Row, Divider } from 'antd';
import { OrganizationMemberManager, AuthenticationManager } from '@copilot/data';
import EditableTextDisplay from '@copilot/common/components/forms/common/inputs/editableTextDisplay';
import { OrganizationMemberActions } from '@copilot/common/store/actions/organizationMember';
import PasswordUpdateForm from '@copilot/common/components/forms/user/passwordUpdate';
import notificationManager from '@copilot/common/utils/notificationManager';
import { Config } from '@copilot/common/config';

interface UserSettingsProps {
	user: {
		firstName: string;
		lastName: string;
		email: string;
		phoneNumber: string;
	};
}

interface Field {
	label: string;
	key: keyof UserSettingsProps['user'];
}

const UserSettingsFields: Field[] = [
	{ label: 'First Name', key: 'firstName' },
	{ label: 'Last Name', key: 'lastName' },
	{ label: 'Email', key: 'email' },
];

const UserSettingsEditableFields: Field[] = [
	{ label: 'Phone Number', key: 'phoneNumber' },
];

const UserSettings: React.FC<UserSettingsProps> = (props) => {
	const { user } = props;
	const storeDispatch = useDispatch();
	const updateUserField = React.useCallback(
		(field: keyof UserSettingsProps['user']) => (value: string) => {
			OrganizationMemberManager.updateSelf({ [field]: value }).then((member) => {
				storeDispatch(OrganizationMemberActions.loadOrganizationMember(member));
			});
		},
		[]
	);
	const updatePassword = React.useCallback<
		React.ComponentProps<typeof PasswordUpdateForm>['onSubmit']
	>((values) => {
		AuthenticationManager.setPassword(
			values.oldPassword,
			values.newPassword,
			values.confirmPassword
		)
			.then(() =>
				notificationManager.showSuccessNotification({
					message: 'Saved',
					description: 'Your password has been updated.',
				})
			)
			.catch(() => {
				notificationManager.showErrorNotification({
					message: 'Failed',
					description: 'Please refresh the page and try again',
				});
			});
	}, []);
	const fields = React.useMemo(() => [
		...UserSettingsFields.map((f) => (
			<React.Fragment key={f.key}>
				<Row key={f.key}>
					<Col span={3}>
						<label>{f.label}</label>
					</Col>
					<Col span={3}>{props.user[f.key]}</Col>
				</Row>
				<br />
			</React.Fragment>
		)),
		...UserSettingsEditableFields.map((f) => (
			<React.Fragment key={f.key}>
				<Row key={f.key}>
					<Col span={3}>
						<label>{f.label}</label>
					</Col>
					<Col span={3}>
						<EditableTextDisplay
							value={props.user[f.key]}
							confirmHandler={updateUserField(f.key)}
							confirmOnBlur
						>
							{props.user[f.key]}
						</EditableTextDisplay>
					</Col>
				</Row>
				<br />
			</React.Fragment>
		)),
	], [UserSettingsFields, UserSettingsEditableFields, user]);
	return (
		<Row>
			<Col span={24}>{fields}</Col>
			{!Config.isB2CEnabled && (
				<>
					<Divider />
					<Col xs={4} sm={6}>
						<PasswordUpdateForm onSubmit={updatePassword} />
					</Col>
				</>
			)}
		</Row>
	);
};

export default UserSettings;
